import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGeri, useSekme } from '@/lib/geri';
import api from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Check, AlertTriangle, Search, Camera, CameraOff, Pencil, Trash2, X, Send, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { trNorm } from '@/lib/arama';
import { trSayi, trTutar } from '@/lib/sayi';
import { olcuBirimleri } from '@/lib/birimler';
import { useProducts } from '@/contexts/ProductsContext';

const statusLabels = { draft: 'Taslak', in_progress: 'Devam Ediyor', pending_warehouse: 'Depo Onayı Bekliyor',
  pending_approval: 'Yönetici Onayı Bekliyor', completed: 'Kesinleşti' };
const statusColors = {
  draft: 'bg-[#e8dfd0] text-[#3F3E39]',
  in_progress: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  pending_warehouse: 'bg-amber-100 text-amber-800',
  pending_approval: 'bg-[#2e2d29]/10 text-[#2e2d29]',
  completed: 'bg-[#3d6e4e]/10 text-[#3d6e4e]'
};

export default function StockCountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/stock/counts');
  const [count, setCount] = useState(null);
  usePageTitle('Sayım Detayı');
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Sayılmayanların SIFIRLANACAĞINI gördüğünü onaylama kutusu; pencere her
  // açılışta işaretsiz başlar — bir önceki açılıştaki işaret taşınmasın.
  const [sifirOnay, setSifirOnay] = useState(false);
  useEffect(() => { if (showConfirm) setSifirOnay(false); }, [showConfirm]);
  const [showRevert, setShowRevert] = useState(false);
  // SAYILANLAR / SAYILMAYANLAR AYRI SEKMEDE (kullanıcı, 2026-10-02): tek
  // listede sayılanlar üstte birikiyor, sayılmayanlar yüzlerce satır aşağıda
  // kalıyordu (Ana Mutfak 583 ürün). Varsayılan SAYILMAYANLAR — sayım yapan
  // kişinin işi orası. Sekme adreste (`?liste=`): ürün kaydedip ya da sayfayı
  // yenileyip dönen kullanıcı aynı sekmede kalsın.
  const [listeSekmesi, listeSekmesiSec] = useSekme('sayilmayan', 'liste');

  // Quick entry state
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [entryQty, setEntryQty] = useState('');
  // ALTERNATİF BİRİMLE SAYIM (kullanıcı, 2026-10-02): "3 koli", "750 g".
  // Seçenekler kartın dönüşümleri + aynı ölçü ailesi; ana birime çeviriyi
  // SUNUCU yapar (`girilen` → `to_main_qty`), buradaki çarpan yalnızca önizleme.
  const [entryUnit, setEntryUnit] = useState('');
  const { products } = useProducts();
  const [units, setUnits] = useState([]);
  useEffect(() => { api.get('/admin/units').then(r => setUnits(r.data || [])).catch(() => {}); }, []);
  const kartBul = (pid) => products.find(p => p.id === pid) || null;
  const birimSecenekleriFor = (item) => {
    const kart = item ? kartBul(item.product_card_id) : null;
    if (!kart) return [{ deger: item?.unit || '', carpan: 1, etiket: item?.unit || '' }];
    return olcuBirimleri(kart, units);
  };
  const [showSaved, setShowSaved] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editQty, setEditQty] = useState('');

  // Barcode assign flow
  const [unmatchedBarcode, setUnmatchedBarcode] = useState(null);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignResults, setAssignResults] = useState([]);
  // Barcode scanner
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const searchRef = useRef(null);
  const qtyRef = useRef(null);

  // Load count
  useEffect(() => {
    api.get(`/stock/counts/${id}`).then(r => setCount(r.data)).catch(() => {
      toast.error('Sayim yuklenemedi');
      navigate('/erp/stock/counts');
    });
  }, [id, navigate]);

  const isEditable = count?.status === 'in_progress';
  // Sayım sürerken sistemdeki miktar gizlenir (backend de göndermez): sayan kişi
  // sistem değerini görürse sayım bir doğrulama olmaktan çıkar.
  const sistemGizli = !!count?._sistem_miktari_gizli;
  const isPendingApproval = count?.status === 'pending_approval';
  // DEPO ONAYI aşaması bir dönem bu sayfada HİÇ çizilmiyordu (ne liste ne
  // düğme): onay yalnızca listeden, içeriğe bakılmadan veriliyordu.
  const isPendingWarehouse = count?.status === 'pending_warehouse';
  const isCompleted = count?.status === 'completed';
  const isReadonly = isPendingApproval || isPendingWarehouse || isCompleted;
  // Red: SEBEP ZORUNLU, sayımı yapan kişi sayfanın başında görür (2026-10-03)
  const [redAcik, setRedAcik] = useState(false);
  const [redSebebi, setRedSebebi] = useState('');

  // Items that have been counted (reverse chronological)
  const countedItems = (count?.items || [])
    .map((item, idx) => ({ ...item, _idx: idx }))
    .filter(item => item.counted_quantity != null)
    .reverse();

  // Items not yet counted (original order)
  const uncountedItems = (count?.items || [])
    .map((item, idx) => ({ ...item, _idx: idx }))
    .filter(item => item.counted_quantity == null);

  // All items with index (for readonly view)
  const allItemsIndexed = (count?.items || [])
    .map((item, idx) => ({ ...item, _idx: idx }));

  const totalItems = count?.items?.length || 0;
  const countedCount = countedItems.length;

  // Search products in count items (all items, not just uncounted)
  useEffect(() => {
    if (!count || searchText.length < 2) { setSearchResults([]); return; }
    const sl = trNorm(searchText);
    const results = count.items.filter(item =>
      trNorm(item.product_name).includes(sl) ||
      trNorm(item.product_code).includes(sl)
    ).slice(0, 8);
    setSearchResults(results);
  }, [searchText, count]);

  // Sayımda OLMAYAN ürünleri katalogdan ara.
  //
  // Sayım listesi yalnızca depoda stok satırı olan ürünlerden kuruluyor;
  // hiç stok görmemiş bir ürün (yarı mamullerin neredeyse tamamı) listeye
  // giremiyordu. Satın alınmadıkları için stoğa girişleri üretimden olmalı,
  // üretim emri modülü ise henüz kullanılmıyor — kısır döngü.
  //
  // GEÇİCİ: üretim akışı çalışınca bu bölüm kaldırılacak.
  const [katalogSonuc, setKatalogSonuc] = useState([]);
  const [ekleniyor, setEkleniyor] = useState(null);
  useEffect(() => {
    if (!count || !isEditable || searchText.length < 2) { setKatalogSonuc([]); return; }
    let iptal = false;
    const zaman = setTimeout(() => {
      api.get('/products', { params: { search: searchText, per_page: 20 } })
        .then(r => {
          if (iptal) return;
          const sayimda = new Set((count.items || []).map(i => i.product_card_id));
          setKatalogSonuc((r.data.items || r.data || [])
            .filter(p => !sayimda.has(p.id)).slice(0, 6));
        })
        .catch(() => !iptal && setKatalogSonuc([]));
    }, 300);
    return () => { iptal = true; clearTimeout(zaman); };
  }, [searchText, count, isEditable]);


  // Barcode scanning
  const startScanning = useCallback(async () => {
    try {
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      const reader = new BrowserMultiFormatReader();
      codeReaderRef.current = reader;
      setScanning(true);

      // Small delay to let video element mount
      setTimeout(async () => {
        if (!videoRef.current) return;
        try {
          await reader.decodeFromVideoDevice(undefined, videoRef.current, async (result) => {
            if (result) {
              const barcode = result.getText();
              // Find matching product by barcode in count items
              let match = count?.items?.find(item =>
                item.product_code === barcode ||
                (item.barcode && item.barcode === barcode)
              );
              // Fallback: search products API by barcode
              if (!match) {
                try {
                  const res = await api.get('/products', { params: { search: barcode } });
                  const product = res.data?.[0];
                  if (product) {
                    match = count?.items?.find(item => item.product_card_id === product.id);
                  }
                } catch {}
              }
              if (match) {
                selectProduct(match);
                if (navigator.vibrate) navigator.vibrate(100);
              } else {
                if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
                setUnmatchedBarcode(barcode);
              }
              stopScanning();
            }
          });
        } catch (err) {
          console.error('Camera error:', err);
          toast.error('Kamera erisim hatasi');
          setScanning(false);
        }
      }, 100);
    } catch (err) {
      toast.error('Barkod tarayici yuklenemedi');
    }
  }, [count]);

  const stopScanning = useCallback(() => {
    if (codeReaderRef.current) {
      codeReaderRef.current = null;
    }
    setScanning(false);
  }, []);

  // Barcode assign: search products for assignment
  useEffect(() => {
    if (!unmatchedBarcode || assignSearch.length < 2) { setAssignResults([]); return; }
    const sl = trNorm(assignSearch);
    const results = (count?.items || []).filter(item =>
      trNorm(item.product_name).includes(sl) ||
      trNorm(item.product_code).includes(sl)
    ).slice(0, 8);
    setAssignResults(results);
  }, [assignSearch, unmatchedBarcode, count]);

  const selectProduct = useCallback((item) => {
    setSelectedItem(item);
    setSearchText('');
    setSearchResults([]);
    // Daha önce başka birimle sayıldıysa o giriş geri gelir ("3 koli")
    if (item.girilen && item.counted_quantity != null) {
      setEntryQty(String(item.girilen.miktar));
      setEntryUnit(item.girilen.birim);
    } else {
      setEntryQty(item.counted_quantity != null ? String(item.counted_quantity) : '');
      setEntryUnit('');
    }
    setTimeout(() => qtyRef.current?.focus(), 50);
  }, []);

  // Ürünü sayıma ekle. Sistem miktarını SUNUCU belirler — istemciden
  // gönderilseydi sayımcı farkı sıfırlayabilirdi.
  const sayimaEkle = useCallback(async (urun) => {
    setEkleniyor(urun.id);
    try {
      const res = await api.post(`/stock/counts/${count.id}/items`,
        { product_card_id: urun.id });
      setCount(res.data);
      const yeni = (res.data.items || []).find(i => i.product_card_id === urun.id);
      setKatalogSonuc([]);
      if (yeni) selectProduct(yeni);
      toast.success(`${urun.name} sayıma eklendi`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ürün eklenemedi');
    }
    setEkleniyor(null);
  }, [count, selectProduct]);

  const assignBarcodeToProduct = useCallback(async (item) => {
    if (!unmatchedBarcode) return;
    try {
      await api.patch(`/products/${item.product_card_id}`, { barcode: unmatchedBarcode });
      toast.success(`Barkod ${item.product_name} urunune atandi`);
      if (navigator.vibrate) navigator.vibrate(100);
      setUnmatchedBarcode(null);
      setAssignSearch('');
      setAssignResults([]);
      selectProduct(item);
    } catch (err) {
      toast.error('Barkod atama hatasi');
    }
  }, [unmatchedBarcode, selectProduct]);

  const saveEntry = useCallback(async () => {
    if (!selectedItem || !entryQty) return;
    const qty = parseFloat(entryQty);
    if (isNaN(qty) || qty < 0) { toast.error('Gecersiz miktar'); return; }

    const items = [...count.items];
    const idx = items.findIndex(i => i.product_card_id === selectedItem.product_card_id);
    if (idx === -1) return;

    // Ana birim dışı seçildiyse girilen değer + birim gider, sunucu çevirir.
    // Önizleme çarpanıyla yerelde de çevriliyor ki liste anında doğru görünsün.
    const secenek = birimSecenekleriFor(items[idx]).find(o => o.deger === entryUnit);
    const anaBirimde = !entryUnit || !secenek || secenek.carpan === 1;
    items[idx] = {
      ...items[idx],
      counted_quantity: anaBirimde ? qty : qty * secenek.carpan,
      girilen: anaBirimde ? null : { miktar: qty, birim: entryUnit },
    };
    const diff = items[idx].counted_quantity - (items[idx].system_quantity ?? 0);
    items[idx].difference = diff;
    items[idx].difference_value = diff * (items[idx].unit_cost || 0);

    setLoading(true);
    try {
      const res = await api.patch(`/stock/counts/${count.id}`, { items });
      setCount(res.data);
      setSelectedItem(null);
      setEntryQty('');
      setEntryUnit('');
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 1200);
      setTimeout(() => searchRef.current?.focus(), 100);
    } catch (err) { toast.error(err.response?.data?.detail || 'Kayit hatasi'); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, entryQty, entryUnit, count, products, units]);

  const startEdit = useCallback((item) => {
    setEditIndex(item._idx);
    setEditQty(String(item.counted_quantity ?? ''));
  }, []);

  const saveEdit = useCallback(async () => {
    if (editIndex == null) return;
    const qty = parseFloat(editQty);
    if (isNaN(qty) || qty < 0) { toast.error('Gecersiz miktar'); return; }

    const items = [...count.items];
    // Satır içi düzeltme ANA BİRİMDE; önceki "3 koli" girişi artık geçerli değil
    items[editIndex] = { ...items[editIndex], counted_quantity: qty, girilen: null };
    const diff = qty - (items[editIndex].system_quantity ?? 0);
    items[editIndex].difference = diff;
    items[editIndex].difference_value = diff * (items[editIndex].unit_cost || 0);

    try {
      const res = await api.patch(`/stock/counts/${count.id}`, { items });
      setCount(res.data);
      setEditIndex(null);
      toast.success('Guncellendi');
    } catch (err) { toast.error('Hata'); }
  }, [editIndex, editQty, count]);

  const deleteEntry = useCallback(async (item) => {
    const items = [...count.items];
    items[item._idx] = { ...items[item._idx], counted_quantity: null, girilen: null };
    items[item._idx].difference = 0;
    items[item._idx].difference_value = 0;

    try {
      const res = await api.patch(`/stock/counts/${count.id}`, { items });
      setCount(res.data);
      toast.success('Silindi');
    } catch (err) { toast.error('Hata'); }
  }, [count]);

  const submitForApproval = useCallback(async () => {
    setLoading(true);
    try {
      // Save items first
      await api.patch(`/stock/counts/${count.id}`, { items: count.items });
      // Then submit
      await api.patch(`/stock/counts/${count.id}/submit`);
      toast.success('Sayim onaya gonderildi');
      setShowConfirm(false);
      const res = await api.get(`/stock/counts/${id}`);
      setCount(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setLoading(false);
  }, [count, id]);

  const approveCount = useCallback(async () => {
    setLoading(true);
    try {
      await api.post(`/stock/counts/${count.id}/complete`);
      toast.success('Sayim kesinlesti, stok hareketleri olusturuldu');
      setShowConfirm(false);
      const res = await api.get(`/stock/counts/${id}`);
      setCount(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setLoading(false);
  }, [count, id]);

  const approveWarehouse = useCallback(async () => {
    setLoading(true);
    try {
      await api.patch(`/stock/counts/${count.id}/approve-warehouse`);
      toast.success('Depo onayı verildi, sayım yönetici onayına gitti');
      const res = await api.get(`/stock/counts/${id}`);
      setCount(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setLoading(false);
  }, [count, id]);

  const rejectCount = useCallback(async () => {
    if (redSebebi.trim().length < 3) { toast.error('Red sebebini yazın'); return; }
    setLoading(true);
    try {
      await api.patch(`/stock/counts/${count.id}/reject`, { reason: redSebebi.trim() });
      setRedAcik(false);
      setRedSebebi('');
      toast.success('Sayım reddedildi, düzeltme için geri gönderildi');
      const res = await api.get(`/stock/counts/${id}`);
      setCount(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setLoading(false);
  }, [count, id, redSebebi]);

  const revertCount = useCallback(async () => {
    setLoading(true);
    try {
      await api.post(`/stock/counts/${count.id}/revert`);
      toast.success('Sayim geri alindi, stok hareketleri silindi');
      setShowRevert(false);
      const res = await api.get(`/stock/counts/${id}`);
      setCount(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setLoading(false);
  }, [count, id]);

  if (!count) return <div className="text-center py-8 text-[#8a7e70]">Yukleniyor...</div>;

  const uncountedCount = totalItems - countedCount;
  // Sayılmayanların sıfırlanınca kaybolacak değeri (sistem miktarı onaylayana görünür)
  // TUTARLAR SUNUCUDAN (`stock.sayim_tutarlari`), liste ile AYNI kaynak:
  // sayılmayan kesinleşmede sıfırlandığı için stok değeri yalnızca sayılan
  // miktarlardan, fark sıfırlananlar dahil. Bir dönem burada sayılmayan SİSTEM
  // miktarıyla değerleniyor, fark da kayıttaki eski toplamdan okunuyordu.
  const sifirlanacakDeger = count.sifirlanacak_degeri || 0;
  const totalDiff = count.fark_tutari || 0;
  const totalStockValue = count.sayim_degeri || 0;

  return (
    <div data-testid="stock-count-detail-page" className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" onClick={geri}
            className="text-[#8a7e70] hover:text-[#1a1916] flex-shrink-0" data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a1916] tracking-tight truncate">
              Sayim — {count.warehouse_name || 'Depo'}
            </h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className={`status-badge ${statusColors[count.status]}`}>
                {statusLabels[count.status]}
              </span>
              <span className="text-sm text-[#8a7e70]">
                {new Date(count.count_date).toLocaleDateString('tr-TR')}
              </span>
              <span className="text-sm font-semibold text-[#1a1916]">
                {isEditable
                  ? `${countedCount} / ${totalItems} urun sayildi`
                  : `${totalItems} urun`
                }
              </span>
            </div>
          </div>
        </div>
        {isEditable && (
          <div className="flex-shrink-0 self-end sm:self-auto">
            {/* Sayılmayan ürün GÖNDERMEYİ ENGELLEMEZ (kullanıcı, 2026-10-02):
                mutfak 249 ürünü sayamadığı için sayımı hiç gönderemiyordu.
                Uyarı kalır; o ürünlerin stoğu kesinleşmede SIFIRLANIR. */}
            <Button onClick={() => setShowConfirm(true)}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold h-11 px-5 disabled:opacity-40"
              data-testid="submit-for-approval-btn">
              <Send size={16} className="mr-2" /> Onaya Gönder
            </Button>
            {uncountedCount > 0 && (
              <p className="text-xs text-[#8a5c00] mt-1 text-right" data-testid="sayilmayan-uyari">
                {uncountedCount} ürün sayılmadı — onaylanınca stokları 0 olur
              </p>
            )}
          </div>
        )}
        {(isPendingApproval || isPendingWarehouse) && (
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div className="flex gap-2">
            <Button onClick={() => setRedAcik(true)} variant="outline" disabled={loading}
              className="border-[#7a3535] text-[#7a3535] hover:bg-[#7a3535]/10 font-semibold h-11"
              data-testid="reject-count-btn">
              <X size={16} className="mr-2" /> Reddet
            </Button>
            {isPendingWarehouse ? (
              <Button onClick={approveWarehouse} disabled={loading}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold h-11 px-5"
                data-testid="approve-warehouse-btn">
                <Check size={16} className="mr-2" /> Depo Onayla
              </Button>
            ) : (
              <Button onClick={() => setShowConfirm(true)} disabled={loading}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold h-11 px-5"
                data-testid="approve-count-btn">
                <Check size={16} className="mr-2" /> Onayla
              </Button>
            )}
          </div>
          {/* Onaylayan, sayılmadan gelen ürünleri bilerek onaylasın */}
          {uncountedCount > 0 && (
            <p className="text-xs text-[#8a5c00]" data-testid="sayilmayan-uyari-onay">
              {uncountedCount} ürün sayılmadan gönderildi — onaylanınca stokları 0 olur
            </p>
          )}
          </div>
        )}
        {isCompleted && (
          <Button onClick={() => setShowRevert(true)} variant="outline" disabled={loading}
            className="border-[#7a3535] text-[#7a3535] hover:bg-[#7a3535]/10 font-semibold h-11 flex-shrink-0"
            data-testid="revert-count-btn">
            <Undo2 size={16} className="mr-2" /> Geri Al
          </Button>
        )}
      </div>

      {/* Reddedilen sayım: sebep, kim, ne zaman — yeniden gönderilince kalkar */}
      {isEditable && count.rejection_reason && (
        <div className="mb-4 rounded-lg border border-[#c98d8d] bg-[#fff0f0] px-4 py-3 text-sm text-[#7a3535]"
          data-testid="red-bandi">
          <p className="font-semibold flex items-center gap-2">
            <AlertTriangle size={16} /> Sayım reddedildi — düzeltip yeniden onaya gönderin
          </p>
          <p className="mt-1 text-[#3F3E39] whitespace-pre-line">{count.rejection_reason}</p>
          <p className="mt-1 text-xs text-[#8a7e70]">
            {count.rejected_by_name || ''}{count.rejected_at ? ` · ${new Date(count.rejected_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
          </p>
        </div>
      )}

      {/* ─── QUICK ENTRY (only in_progress) ─── */}
      {isEditable && (
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm mb-6" data-testid="quick-entry-card">
          <CardContent className="p-4 md:p-6">
            {/* Search + Barcode */}
            {!selectedItem ? (
              <div className="space-y-3">
                <div className="relative">
                  <Search size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
                  <Input
                    ref={searchRef}
                    autoFocus
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Urun ara veya barkod okut..."
                    className="pl-11 pr-14 h-14 text-lg bg-white border-[#d0c4b0] rounded-lg"
                    data-testid="count-search-input"
                  />
                  <button
                    onClick={scanning ? stopScanning : startScanning}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-md transition-colors ${scanning ? 'bg-[#7a3535] text-white' : 'bg-[#e8dfd0] text-[#3F3E39] hover:bg-[#d0c4b0]'}`}
                    data-testid="barcode-scan-btn"
                  >
                    {scanning ? <CameraOff size={20} /> : <Camera size={20} />}
                  </button>
                </div>

                {/* Camera view */}
                {scanning && (
                  <div className="relative rounded-lg overflow-hidden border border-[#d0c4b0] bg-black" data-testid="camera-view">
                    <video ref={videoRef} className="w-full h-48 object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-48 h-32 border-2 border-[#3d6e4e] rounded-lg opacity-60" />
                    </div>
                  </div>
                )}

                {/* Search results */}
                {searchResults.length > 0 && (
                  <div className="border border-[#d0c4b0] rounded-lg bg-white overflow-hidden" data-testid="search-results">
                    {searchResults.map((item) => {
                      const alreadyCounted = item.counted_quantity != null;
                      return (
                        <button
                          key={item.product_card_id}
                          onClick={() => selectProduct(item)}
                          className="w-full flex items-center justify-between p-3 md:p-4 hover:bg-[#e8dfd0]/50 transition-colors border-b border-[#d0c4b0] last:border-b-0 text-left"
                          data-testid={`search-result-${item.product_code}`}
                        >
                          <div className="min-w-0">
                            <p className="font-semibold text-[#1a1916] truncate">{item.product_name}</p>
                            <p className="text-xs text-[#8a7e70] font-mono">{item.product_code}</p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            {alreadyCounted ? (
                              <>
                                <p className="text-xs text-[#3d6e4e] font-semibold">Sayildi</p>
                                <p className="font-semibold text-[#1a1916]">{trSayi(item.counted_quantity)} {item.unit}
                                  {item.girilen && <span className="block text-[11px] font-normal text-[#8a7e70]">({trSayi(item.girilen.miktar)} {item.girilen.birim})</span>}
                                </p>
                              </>
                            ) : sistemGizli ? (
                              <p className="text-xs text-[#b5aa9a] italic">sayılmadı</p>
                            ) : (
                              <>
                                <p className="text-xs text-[#8a7e70]">Sistem</p>
                                <p className="font-semibold text-[#1a1916]">{item.system_quantity?.toFixed(1)} {item.unit}</p>
                              </>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Sayımda olmayan ürünler — katalogdan ekle.
                    GEÇİCİ: üretim akışı çalışınca kaldırılacak (bkz. sayimaEkle). */}
                {katalogSonuc.length > 0 && (
                  <div className="border border-[#2e6fa0]/30 rounded-lg bg-[#2e6fa0]/5 overflow-hidden"
                    data-testid="katalog-sonuc">
                    <p className="px-3 pt-2.5 pb-1.5 text-xs font-semibold text-[#2e6fa0]">
                      Sayım listesinde yok — eklemek için dokunun
                    </p>
                    {katalogSonuc.map((u) => (
                      <button key={u.id} type="button" disabled={ekleniyor === u.id}
                        onClick={() => sayimaEkle(u)}
                        className="w-full flex items-center justify-between p-3 md:p-4 text-left
                                   hover:bg-[#2e6fa0]/10 transition-colors border-t border-[#2e6fa0]/20
                                   disabled:opacity-50"
                        data-testid={`katalog-ekle-${u.code}`}>
                        <div className="min-w-0">
                          <p className="font-semibold text-[#1a1916] truncate">{u.name}</p>
                          <p className="text-xs text-[#8a7e70] font-mono">{u.code}</p>
                        </div>
                        <span className="text-xs font-semibold text-[#2e6fa0] flex-shrink-0 ml-3">
                          {ekleniyor === u.id ? 'ekleniyor…' : `+ Ekle (${u.unit || ''})`}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Unmatched barcode - assign to product */}
                {unmatchedBarcode && (
                  <div className="border border-[#7a6a2e]/30 rounded-lg bg-[#7a6a2e]/5 p-4 space-y-3" data-testid="barcode-assign-section">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-[#7a6a2e]">Bu barkod tanimli degil</p>
                        <p className="text-xs text-[#8a7e70] font-mono mt-0.5">{unmatchedBarcode}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => { setUnmatchedBarcode(null); setAssignSearch(''); }}
                        className="text-[#8a7e70] h-8 w-8" data-testid="dismiss-barcode-btn">
                        <X size={16} />
                      </Button>
                    </div>
                    <Input
                      value={assignSearch}
                      onChange={(e) => setAssignSearch(e.target.value)}
                      placeholder="Barkodu atamak icin urun ara..."
                      className="h-11 bg-white border-[#d0c4b0]"
                      autoFocus
                      data-testid="barcode-assign-search"
                    />
                    {assignResults.length > 0 && (
                      <div className="border border-[#d0c4b0] rounded-lg bg-white overflow-hidden">
                        {assignResults.map((item) => (
                          <button key={item.product_card_id}
                            onClick={() => assignBarcodeToProduct(item)}
                            className="w-full flex items-center justify-between p-3 hover:bg-[#e8dfd0]/50 transition-colors border-b border-[#d0c4b0] last:border-b-0 text-left"
                            data-testid={`assign-result-${item.product_code}`}>
                            <div className="min-w-0">
                              <p className="font-semibold text-[#1a1916] truncate">{item.product_name}</p>
                              <p className="text-xs text-[#8a7e70] font-mono">{item.product_code}</p>
                            </div>
                            <span className="text-xs font-semibold text-[#3d6e4e] flex-shrink-0 ml-3">Ata</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Saved flash */}
                {showSaved && (
                  <div className="flex items-center justify-center gap-2 py-3 text-[#3d6e4e] animate-fade-in" data-testid="saved-flash">
                    <Check size={20} strokeWidth={2.5} />
                    <span className="font-semibold">Kaydedildi</span>
                  </div>
                )}
              </div>
            ) : (
              /* Selected product - quantity entry */
              <div className="space-y-4" data-testid="qty-entry-section">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-lg font-bold text-[#1a1916] truncate">{selectedItem.product_name}</p>
                    <p className="text-sm text-[#8a7e70]">
                      {sistemGizli
                        ? <span className="italic text-[#b5aa9a]">Birim: {selectedItem.unit}</span>
                        : <>Sistem: <span className="font-semibold text-[#1a1916]">{selectedItem.system_quantity?.toFixed(2)} {selectedItem.unit}</span></>}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => { setSelectedItem(null); searchRef.current?.focus(); }}
                    className="text-[#8a7e70] flex-shrink-0" data-testid="cancel-entry-btn">
                    <X size={18} />
                  </Button>
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    {/* step="any": olculen deger; sabit adim tarayicida "adim
                        uyusmazligi" yapip formu sessizce engelliyor */}
                    <Input
                      ref={qtyRef}
                      type="number"
                      inputMode="decimal"
                      step="any"
                      value={entryQty}
                      onChange={(e) => setEntryQty(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEntry(); }}
                      placeholder="0.00"
                      className="h-16 text-2xl font-bold bg-white border-[#d0c4b0] text-center rounded-lg"
                      data-testid="count-qty-input"
                    />
                    {(() => {
                      const secenekler = birimSecenekleriFor(selectedItem);
                      const ana = kartBul(selectedItem.product_card_id)?.unit || selectedItem.unit;
                      const secili = secenekler.find(o => o.deger === entryUnit);
                      const q = parseFloat(entryQty);
                      if (secenekler.length <= 1) {
                        return <p className="text-center text-sm text-[#8a7e70] mt-1 font-medium">{selectedItem.unit}</p>;
                      }
                      return (
                        <div className="mt-1.5">
                          <select value={entryUnit || ana} onChange={(e) => setEntryUnit(e.target.value === ana ? '' : e.target.value)}
                            className="w-full h-11 rounded-lg border border-[#d0c4b0] bg-white text-center text-base font-medium text-[#3F3E39]"
                            data-testid="count-unit-select">
                            {secenekler.map(o => (
                              <option key={o.deger} value={o.deger}>
                                {o.deger}{o.carpan !== 1 ? ` (1 = ${trSayi(o.carpan, 4)} ${ana})` : ''}
                              </option>
                            ))}
                          </select>
                          {secili && secili.carpan !== 1 && q > 0 && (
                            <p className="text-center text-xs text-[#3d6e4e] mt-1" data-testid="count-unit-preview">
                              = {trSayi(q * secili.carpan, 4)} {ana}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <Button onClick={saveEntry} disabled={loading || !entryQty}
                    className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold h-16 px-8 rounded-lg text-lg"
                    data-testid="save-entry-btn">
                    <Check size={20} className="mr-2" /> Kaydet
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── CHECKLIST / RESULTS ─── */}
      <div className="space-y-6" data-testid="count-checklist">

        {/* ── READONLY VIEW: pending_approval or completed ── */}
        {isReadonly && (
          <div className="space-y-2">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#8a7e70] px-1">
              Sayim Sonuclari ({totalItems} urun)
            </h2>
            {allItemsIndexed.map((item) => {
              // Sayılmayan ürün SİSTEM miktarıyla "sayılmış" gibi gösteriliyordu;
              // oysa kesinleşmede 0'a iner. Sayılmayan 0 ve fark −sistem gösterilir.
              const sayilmadi = item.counted_quantity == null || item.otomatik_sifir;
              const counted = item.counted_quantity != null ? item.counted_quantity : 0;
              const diff = item.counted_quantity != null ? (item.difference || 0) : -(item.system_quantity || 0);
              const hasDiff = diff !== 0;
              return (
                <Card key={item._idx}
                  className={`bg-[#faf7f2] border-[#d0c4b0] ${hasDiff ? (diff < 0 ? 'border-l-4 border-l-[#7a3535]' : 'border-l-4 border-l-[#3d6e4e]') : ''}`}
                  data-testid={`result-item-${item.product_code}`}>
                  <CardContent className="p-3 md:p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        item.counted_quantity != null ? 'bg-[#3d6e4e]/15' : 'border-2 border-[#d0c4b0]'
                      }`}>
                        {item.counted_quantity != null && <Check size={14} className="text-[#3d6e4e]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#1a1916] truncate">{item.product_name}</p>
                        <p className="text-xs text-[#8a7e70] font-mono">{item.product_code}</p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#1a1916]">
                            {trSayi(counted)} <span className="text-sm font-normal text-[#8a7e70]">{item.unit}</span>
                          </p>
                          {sayilmadi && <p className="text-[11px] font-semibold text-[#8a5c00]">sayılmadı → 0</p>}
                          {item.girilen && !sayilmadi && <p className="text-[11px] text-[#8a7e70]">({trSayi(item.girilen.miktar)} {item.girilen.birim})</p>}
                          <p className="text-xs text-[#8a7e70]">Sistem: {trSayi(item.system_quantity || 0)}</p>
                        </div>
                        {hasDiff && (
                          <div className={`w-16 text-center py-1 rounded-md text-sm font-bold ${
                            diff < 0 ? 'bg-[#7a3535]/10 text-[#7a3535]' : 'bg-[#3d6e4e]/10 text-[#3d6e4e]'
                          }`}>
                            {diff > 0 ? '+' : ''}{trSayi(diff, 2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* ── EDITABLE VIEW: in_progress ── */}
        {isEditable && (
          <>
            <div className="sticky top-14 lg:top-0 z-20 -mx-1 px-1 py-1 bg-[#f5f0e8]/95 backdrop-blur-sm">
              <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-[#e8dfd0]" role="tablist"
                data-testid="sayim-sekmeleri">
                {[['sayilmayan', 'Sayılmayanlar', uncountedItems.length],
                  ['sayilan', 'Sayılanlar', countedItems.length]].map(([deger, ad, adet]) => (
                  <button key={deger} type="button" role="tab" aria-selected={listeSekmesi === deger}
                    onClick={() => listeSekmesiSec(deger)}
                    className={`h-11 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2
                      ${listeSekmesi === deger
                        ? 'bg-white text-[#1a1916] shadow-sm'
                        : 'text-[#6b5f4f] hover:bg-white/50'}`}
                    data-testid={`sayim-sekme-${deger}`}>
                    {deger === 'sayilan' && <Check size={14} className="text-[#3d6e4e]" />}
                    {ad}
                    <span className={`min-w-[28px] px-1.5 h-6 rounded-full text-xs font-bold inline-flex items-center justify-center
                      ${deger === 'sayilan' ? 'bg-[#3d6e4e]/15 text-[#3d6e4e]' : 'bg-[#8a5c00]/10 text-[#8a5c00]'}`}>
                      {adet}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Listeye ALINMAYANLAR (mamul, taze yarı mamul, stoğu 0) — sayılmayan
                SIFIRLANDIĞI için liste bilerek dar; gerekirse yukarıdan arayıp eklenir. */}
            {count.kapsam_disi && Object.values(count.kapsam_disi).some(n => n > 0) && (
              <p className="text-[11px] text-[#8a7e70] px-1 -mt-3" data-testid="kapsam-disi-notu">
                Listeye alınmadı:{' '}
                {[['mamul', 'mamul'], ['taze_yari_mamul', 'taze yarı mamul'], ['stok_sifir', 'stoğu 0']]
                  .filter(([k]) => count.kapsam_disi[k] > 0)
                  .map(([k, ad]) => `${count.kapsam_disi[k]} ${ad}`).join(' · ')}
                {' '}— gerekirse yukarıdaki aramadan ekleyin.
              </p>
            )}

            {/* Counted items */}
            {listeSekmesi === 'sayilan' && (
            <div className="space-y-2" data-testid="sayilan-liste">
              {countedItems.length === 0 && (
                <p className="text-center text-sm text-[#8a7e70] py-8">Henüz sayılan ürün yok</p>
              )}
              {countedItems.map((item) => {
                const diff = item.difference || 0;
                const isEditing = editIndex === item._idx;
                // Kenar rengi de farkı ele verdiği için sayım sırasında nötr
                return (
                  <Card key={item._idx} className={`bg-[#faf7f2] border-[#d0c4b0] border-l-4 ${
                    sistemGizli ? 'border-l-[#8a7e70]'
                      : diff < 0 ? 'border-l-[#7a3535]' : diff > 0 ? 'border-l-[#3d6e4e]' : 'border-l-[#8a7e70]'}`}
                    data-testid={`counted-item-${item.product_code}`}>
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-[#3d6e4e]/15 flex items-center justify-center flex-shrink-0">
                          <Check size={14} className="text-[#3d6e4e]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#1a1916] truncate">{item.product_name}</p>
                          <p className="text-xs text-[#8a7e70] font-mono">{item.product_code}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <Input type="number" step="any" value={editQty}
                                onChange={(e) => setEditQty(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditIndex(null); }}
                                className="w-20 h-9 text-center bg-white border-[#d0c4b0]"
                                autoFocus data-testid={`edit-qty-input-${item._idx}`} />
                              <Button size="icon" variant="ghost" onClick={saveEdit}
                                className="h-9 w-9 text-[#3d6e4e]" data-testid={`edit-save-${item._idx}`}>
                                <Check size={16} />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => setEditIndex(null)}
                                className="h-9 w-9 text-[#8a7e70]">
                                <X size={16} />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="text-right">
                                <p className="text-lg font-bold text-[#1a1916]">
                                  {trSayi(item.counted_quantity)} <span className="text-sm font-normal text-[#8a7e70]">{item.unit}</span>
                                </p>
                                {item.girilen && (
                                  <p className="text-[11px] text-[#8a7e70]" data-testid={`girilen-${item._idx}`}>
                                    ({trSayi(item.girilen.miktar)} {item.girilen.birim})
                                  </p>
                                )}
                                {!sistemGizli && (
                                  <p className="text-xs text-[#8a7e70]">Sistem: {item.system_quantity?.toFixed(1)}</p>
                                )}
                              </div>
                              {/* Fark yalnızca onay aşamasında görünür; sayım
                                  sırasında gösterilirse sistem miktarı dolaylı
                                  olarak ele verilir (sayılan − fark = sistem). */}
                              {!sistemGizli && (
                                <div className={`w-16 text-center py-1 rounded-md text-sm font-bold ${
                                  diff < 0 ? 'bg-[#7a3535]/10 text-[#7a3535]' :
                                  diff > 0 ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' :
                                  'bg-[#e8dfd0] text-[#8a7e70]'
                                }`}>
                                  {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                                </div>
                              )}
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" onClick={() => startEdit(item)}
                                  className="h-9 w-9 text-[#8a7e70] hover:text-[#1a1916]" data-testid={`edit-item-${item._idx}`}>
                                  <Pencil size={14} />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => deleteEntry(item)}
                                  className="h-9 w-9 text-[#8a7e70] hover:text-[#7a3535]" data-testid={`delete-item-${item._idx}`}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            )}

            {/* Uncounted items */}
            {listeSekmesi === 'sayilmayan' && (
              <div className="space-y-2" data-testid="sayilmayan-liste">
                {uncountedItems.length === 0 && (
                  <p className="text-center text-sm text-[#3d6e4e] font-medium py-8">
                    <Check size={16} className="inline mr-1 -mt-0.5" /> Bütün ürünler sayıldı
                  </p>
                )}
                {uncountedItems.map((item) => (
                  <Card key={item._idx}
                    className="border-[#d0c4b0] border-l-4 border-l-transparent bg-white cursor-pointer hover:bg-[#e8dfd0]/30 transition-colors"
                    onClick={() => selectProduct(item)}
                    data-testid={`uncounted-item-${item.product_code}`}>
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full border-2 border-[#d0c4b0] flex items-center justify-center flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#3F3E39] truncate">{item.product_name}</p>
                          <p className="text-xs text-[#8a7e70] font-mono">{item.product_code}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm text-[#8a7e70]">Sistem</p>
                          <p className="font-semibold text-[#3F3E39]">{item.system_quantity?.toFixed(1)} {item.unit}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── FOOTER SUMMARY ─── */}
      <div className="mt-6 p-4 bg-[#faf7f2] border border-[#d0c4b0] rounded-lg" data-testid="count-summary">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#8a7e70]">Stok Değeri</p>
            <p className="text-xl font-bold text-[#1a1916] mt-1 tabular-nums" title="Sayılan ürünlerin değeri (sayılmayan kesinleşmede 0 olur)">
              {trTutar(totalStockValue)} TL
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#8a7e70]">Toplam Fark</p>
            {sistemGizli ? (
              <p className="text-sm text-[#b5aa9a] italic mt-2">onay aşamasında görünür</p>
            ) : (
              <p className={`text-xl font-bold mt-1 ${totalDiff < 0 ? 'text-[#7a3535]' : totalDiff > 0 ? 'text-[#3d6e4e]' : 'text-[#1a1916]'}`}>
                {totalDiff > 0 ? '+' : ''}{trTutar(totalDiff)} TL
              </p>
            )}
          </div>
          <div>
            {isEditable ? (
              <>
                <p className="text-xs font-bold uppercase tracking-wider text-[#8a7e70]">Ilerleme</p>
                <p className="text-xl font-bold text-[#1a1916] mt-1">
                  {countedCount} / {totalItems}
                  {uncountedCount > 0 && <span className="text-sm font-normal text-[#8a7e70] ml-1">({uncountedCount} kaldi)</span>}
                </p>
              </>
            ) : (
              <>
                <p className="text-xs font-bold uppercase tracking-wider text-[#8a7e70]">Fark Olan Urun</p>
                <p className="text-xl font-bold text-[#1a1916] mt-1">
                  {allItemsIndexed.filter(i => (i.difference || 0) !== 0).length} <span className="text-sm font-normal text-[#8a7e70]">/ {totalItems} urun</span>
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── RED PENCERESİ ─── */}
      <Dialog open={redAcik} onOpenChange={(a) => { setRedAcik(a); if (!a) setRedSebebi(''); }}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
              <X size={18} className="text-[#7a3535]" /> Sayımı Reddet
            </DialogTitle>
            <DialogDescription className="text-[#3F3E39] text-sm">
              Sayım "Devam Ediyor"a döner, girilen miktarlar korunur, stok değişmez.
              Sayımı yapan kişi bu notu sayım sayfasının başında görür.
            </DialogDescription>
          </DialogHeader>
          <textarea value={redSebebi} onChange={(e) => setRedSebebi(e.target.value)} rows={3}
            placeholder="Örn. Soğuk oda sayılmamış, donuk ürünler tekrar sayılsın"
            className="w-full rounded-md border border-[#d0c4b0] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#7a3535]"
            data-testid="red-sebebi" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRedAcik(false); setRedSebebi(''); }} className="border-[#d0c4b0]">Vazgeç</Button>
            <Button onClick={rejectCount} disabled={loading || redSebebi.trim().length < 3}
              className="bg-[#7a3535] text-white hover:bg-[#5a2525] font-semibold" data-testid="red-onay-btn">
              {loading ? 'İşleniyor...' : 'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── CONFIRM DIALOG ─── */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
              <AlertTriangle size={20} className="text-[#7a6a2e]" />
              {isEditable ? 'Onaya Gonder' : 'Sayimi Kesinlestir'}
            </DialogTitle>
            <DialogDescription className="text-[#3F3E39] pt-3 text-sm leading-relaxed">
              {sistemGizli ? (
                <>
                  <span className="font-semibold text-[#1a1916]">{countedCount} / {totalItems} ürün</span> sayıldı.
                  Fark hesabı onay aşamasında görünecek.
                </>
              ) : (
                <>
                  <span className="font-semibold text-[#1a1916]">{totalItems} urunde</span> toplam{' '}
                  <span className={`font-bold ${totalDiff < 0 ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>
                    {trTutar(totalDiff)} TL
                  </span>{' '}
                  fark tespit edildi.
                </>
              )}
              {uncountedCount > 0 && (
                <span className="block mt-3 rounded-md border border-[#c98d8d] bg-[#fff0f0] px-3 py-2 text-[#7a3535]"
                  data-testid="sayilmayan-onay-uyarisi">
                  <span className="font-semibold">{uncountedCount} ürün sayılmadı.</span>{' '}
                  Sayım onaylanınca bu ürünlerin stoğu <b>0'a indirilir</b>. Depoda olan
                  bir ürün varsa önce sayın.
                  {!sistemGizli && sifirlanacakDeger > 0 && (
                    <span className="block mt-1 font-semibold">
                      Sıfırlanacak stok değeri: {trTutar(sifirlanacakDeger)} TL
                    </span>
                  )}
                </span>
              )}
              {uncountedCount > 0 && (
                <span className="block mt-2 max-h-56 overflow-y-auto rounded-md border border-[#e6dcc9] bg-white divide-y divide-[#f0e9dc]"
                  data-testid="sifirlanacak-liste">
                  {uncountedItems.map(it => (
                    <span key={it._idx} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs">
                      <span className="min-w-0">
                        <span className="block truncate text-[#1a1916]">{it.product_name}</span>
                        <span className="block font-mono text-[10px] text-[#8a7e70]">{it.product_code}</span>
                      </span>
                      {!sistemGizli && (
                        <span className="shrink-0 font-mono tabular-nums text-[#7a3535]">
                          {trSayi(it.system_quantity || 0)} → 0 {it.unit}
                        </span>
                      )}
                    </span>
                  ))}
                </span>
              )}
              {uncountedCount > 0 && (
                <label className="flex items-start gap-2 mt-3 text-sm text-[#1a1916] cursor-pointer select-none">
                  <input type="checkbox" checked={sifirOnay} onChange={e => setSifirOnay(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[#7a3535]" data-testid="sifir-onay-kutusu" />
                  <span>Listeyi gördüm; sayılmayan {uncountedCount} ürünün stoğunun 0 olacağını onaylıyorum.</span>
                </label>
              )}
              <br /><br />
              {isEditable
                ? 'Sayim onay icin yoneticiye gonderilecek. Devam etmek istiyor musunuz?'
                : 'Stok hareketleri otomatik olusturulacak. Kesinlestirmek istiyor musunuz?'
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} className="border-[#d0c4b0]" data-testid="confirm-cancel-btn">
              Iptal
            </Button>
            {isEditable ? (
              <Button onClick={submitForApproval} disabled={loading || (uncountedCount > 0 && !sifirOnay)}
                className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="confirm-submit-btn">
                {loading ? 'Gonderiliyor...' : 'Onaya Gonder'}
              </Button>
            ) : (
              <Button onClick={approveCount} disabled={loading || (uncountedCount > 0 && !sifirOnay)}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold" data-testid="confirm-approve-btn">
                {loading ? 'Isleniyor...' : 'Onayla ve Kesinlestir'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── REVERT DIALOG ─── */}
      <Dialog open={showRevert} onOpenChange={setShowRevert}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
              <Undo2 size={20} className="text-[#7a3535]" />
              Sayimi Geri Al
            </DialogTitle>
            <DialogDescription className="text-[#3F3E39] pt-3 text-sm leading-relaxed">
              Bu islem geri alindiginda:
              <br /><br />
              <span className="text-[#7a3535] font-semibold">Stok miktarlari sayim oncesi degerlere donecek.</span>
              <br />
              Sayima bagli stok hareketleri ve fire kayitlari silinecek.
              <br />
              Sayim "Devam Ediyor" durumuna donecek, tekrar duzenlenebilecek.
              <br /><br />
              Devam etmek istiyor musunuz?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevert(false)} className="border-[#d0c4b0]" data-testid="revert-cancel-btn">
              Iptal
            </Button>
            <Button onClick={revertCount} disabled={loading}
              className="bg-[#7a3535] text-white hover:bg-[#5e2929] font-semibold" data-testid="revert-confirm-btn">
              {loading ? 'Geri aliniyor...' : 'Geri Al'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
